import * as THREE from 'three';
import { io, type Socket } from 'socket.io-client';
import type { Game, GameContext, GameMeta, InputState } from '../types';
import {
  createThreeKit,
  disposeObject,
  makeGridFloor,
  renderThree,
  resizeThree,
  type ThreeKit,
} from '../shared/threeKit';

type RhythmSection = {
  beats: number;
  attacker: 1 | 2;
  label: string;
};

type MatchStartPayload = {
  room: string;
  player: 1 | 2;
  beatMs: number;
  startAt: number;
  sections: RhythmSection[];
  totalBeats: number;
  maxHealth: number;
  round: number;
  p1Wins: number;
  p2Wins: number;
  roundsToWin: number;
};

type RoundStatePayload = {
  round: number;
  beat: number;
  sectionIndex: number;
  p1Health: number;
  p2Health: number;
  p1Combo: number;
  p2Combo: number;
  outcome: string;
};

type RoundTransitionPayload = {
  round: number;
  startAt: number;
  p1Health: number;
  p2Health: number;
  p1Wins: number;
  p2Wins: number;
};

type MatchEndPayload = {
  winner: 0 | 1 | 2;
  p1Wins: number;
  p2Wins: number;
};

function makeFighter(primary: number, accent: number): THREE.Group {
  const group = new THREE.Group();
  const bodyMat = new THREE.MeshStandardMaterial({ color: primary, roughness: 0.55, metalness: 0.18 });
  const accentMat = new THREE.MeshStandardMaterial({ color: accent, roughness: 0.45, metalness: 0.2 });
  const skinMat = new THREE.MeshStandardMaterial({ color: 0xfad9bf, roughness: 0.7, metalness: 0.05 });

  const torso = new THREE.Mesh(new THREE.BoxGeometry(1.3, 1.8, 0.9), bodyMat);
  torso.position.y = 1.8;
  group.add(torso);

  const head = new THREE.Mesh(new THREE.BoxGeometry(1, 1, 1), skinMat);
  head.position.y = 3.1;
  group.add(head);

  const leftArm = new THREE.Mesh(new THREE.BoxGeometry(0.45, 1.2, 0.45), accentMat);
  leftArm.position.set(-1.05, 1.95, 0);
  group.add(leftArm);

  const rightArm = new THREE.Mesh(new THREE.BoxGeometry(0.45, 1.2, 0.45), accentMat);
  rightArm.position.set(1.05, 1.95, 0);
  group.add(rightArm);

  const leftLeg = new THREE.Mesh(new THREE.BoxGeometry(0.5, 1.35, 0.5), bodyMat);
  leftLeg.position.set(-0.42, 0.6, 0);
  group.add(leftLeg);

  const rightLeg = new THREE.Mesh(new THREE.BoxGeometry(0.5, 1.35, 0.5), bodyMat);
  rightLeg.position.set(0.42, 0.6, 0);
  group.add(rightLeg);

  const gloveL = new THREE.Mesh(new THREE.BoxGeometry(0.62, 0.62, 0.62), accentMat);
  gloveL.position.set(-1.2, 1.7, 0.38);
  group.add(gloveL);

  const gloveR = new THREE.Mesh(new THREE.BoxGeometry(0.62, 0.62, 0.62), accentMat);
  gloveR.position.set(1.2, 1.7, 0.38);
  group.add(gloveR);

  return group;
}

export class RhythmDuelGame implements Game {
  readonly meta: GameMeta = {
    id: 'rhythm-duel',
    title: 'BEAT BRAWL (ONLINE)',
    blurb: '1-button rhythm duel: attacker hits on beat, defender blocks on beat.',
    tag: 'MULTIPLAYER',
    status: 'playable',
  };

  private ctx!: GameContext;
  private kit!: ThreeKit;
  private socket!: Socket;
  private disposed = false;

  private fighter1!: THREE.Group;
  private fighter2!: THREE.Group;
  private roleRing1!: THREE.Mesh;
  private roleRing2!: THREE.Mesh;
  private beatBar!: THREE.Mesh;

  private matchState: 'connecting' | 'waiting' | 'playing' | 'disconnected' | 'ended' = 'connecting';
  private room = '';
  private playerNum: 1 | 2 = 1;

  private beatMs = 600;
  private startAt = 0;
  private totalBeats = 0;
  private maxHealth = 18;
  private round = 1;
  private roundsToWin = 2;
  private sections: RhythmSection[] = [];

  private p1Health = 18;
  private p2Health = 18;
  private p1Combo = 0;
  private p2Combo = 0;
  private p1Wins = 0;
  private p2Wins = 0;

  private sectionIndex = 0;
  private outcome = '';
  private hudMessage = 'CONNECTING...';
  private lastSentBeat = Number.NEGATIVE_INFINITY;
  private feedbackTimer = 0;

  async mount(ctx: GameContext): Promise<void> {
    this.ctx = ctx;
    this.kit = createThreeKit(ctx, {
      label: 'Beat Brawl',
      clear: 0x05070f,
      z: 24,
      fov: 46,
      fog: 0x05070f,
      fogNear: 15,
      fogFar: 40,
    });

    this.kit.camera.position.set(0, 4, 24);
    this.kit.camera.lookAt(0, 2, 0);

    const floor = makeGridFloor(22, 12, 0x120d14, 0x53253d);
    floor.position.y = -0.02;
    this.kit.root.add(floor);

    const backWall = new THREE.Mesh(
      new THREE.BoxGeometry(22, 8, 0.5),
      new THREE.MeshStandardMaterial({ color: 0x0f0b12, emissive: 0x180612, emissiveIntensity: 0.7 }),
    );
    backWall.position.set(0, 4, -4.8);
    this.kit.root.add(backWall);

    this.fighter1 = makeFighter(0x38ccff, 0x0a84ff);
    this.fighter1.position.set(-4.2, 0, 0.2);
    this.kit.root.add(this.fighter1);

    this.fighter2 = makeFighter(0xff7a5c, 0xff3366);
    this.fighter2.position.set(4.2, 0, -0.2);
    this.fighter2.rotation.y = Math.PI;
    this.kit.root.add(this.fighter2);

    const ringGeo = new THREE.TorusGeometry(1.15, 0.08, 12, 36);
    this.roleRing1 = new THREE.Mesh(ringGeo, new THREE.MeshStandardMaterial({ color: 0x2fdf84, emissive: 0x1d3f2b }));
    this.roleRing1.rotation.x = Math.PI / 2;
    this.roleRing1.position.set(-4.2, 0.06, 0.2);
    this.kit.root.add(this.roleRing1);

    this.roleRing2 = new THREE.Mesh(ringGeo, new THREE.MeshStandardMaterial({ color: 0x2fdf84, emissive: 0x1d3f2b }));
    this.roleRing2.rotation.x = Math.PI / 2;
    this.roleRing2.position.set(4.2, 0.06, -0.2);
    this.kit.root.add(this.roleRing2);

    this.beatBar = new THREE.Mesh(
      new THREE.BoxGeometry(10, 0.18, 0.5),
      new THREE.MeshStandardMaterial({ color: 0xff4d6d, emissive: 0x5b1430, emissiveIntensity: 0.8 }),
    );
    this.beatBar.position.set(0, 6.8, -1.8);
    this.kit.root.add(this.beatBar);

    const rect = ctx.mount.getBoundingClientRect();
    this.resize(rect.width || 320, rect.height || 480);

    this.socket = io({ path: '/socket.io/' });
    this.wireSocketHandlers();
  }

  private wireSocketHandlers(): void {
    this.socket.on('connect', () => {
      this.matchState = 'connecting';
      this.hudMessage = 'CONNECTING...';
      this.joinQueue();
    });

    this.socket.on('rhythm_waiting', () => {
      this.matchState = 'waiting';
      this.hudMessage = 'WAITING FOR OPPONENT...';
    });

    this.socket.on('rhythm_match_start', (data: MatchStartPayload) => {
      this.room = data.room;
      this.playerNum = data.player;
      this.beatMs = data.beatMs;
      this.startAt = data.startAt;
      this.totalBeats = data.totalBeats;
      this.maxHealth = data.maxHealth;
      this.round = data.round;
      this.roundsToWin = data.roundsToWin;
      this.sections = data.sections;
      this.p1Wins = data.p1Wins;
      this.p2Wins = data.p2Wins;
      this.p1Health = data.maxHealth;
      this.p2Health = data.maxHealth;
      this.p1Combo = 0;
      this.p2Combo = 0;
      this.sectionIndex = 0;
      this.lastSentBeat = Number.NEGATIVE_INFINITY;
      this.matchState = 'playing';
      this.outcome = 'ROUND START';
      this.feedbackTimer = 1.4;
      this.hudMessage = 'ROUND START';
    });

    this.socket.on('rhythm_round_state', (data: RoundStatePayload) => {
      this.round = data.round;
      this.sectionIndex = data.sectionIndex;
      this.p1Health = data.p1Health;
      this.p2Health = data.p2Health;
      this.p1Combo = data.p1Combo;
      this.p2Combo = data.p2Combo;
      this.outcome = data.outcome;
      this.feedbackTimer = 0.9;
      this.lastSentBeat = Math.max(this.lastSentBeat, data.beat);
    });

    this.socket.on('rhythm_round_transition', (data: RoundTransitionPayload) => {
      this.round = data.round;
      this.startAt = data.startAt;
      this.p1Health = data.p1Health;
      this.p2Health = data.p2Health;
      this.p1Wins = data.p1Wins;
      this.p2Wins = data.p2Wins;
      this.p1Combo = 0;
      this.p2Combo = 0;
      this.lastSentBeat = Number.NEGATIVE_INFINITY;
      this.outcome = 'NEXT ROUND';
      this.feedbackTimer = 1.5;
      this.matchState = 'playing';
    });

    this.socket.on('rhythm_match_end', (data: MatchEndPayload) => {
      this.p1Wins = data.p1Wins;
      this.p2Wins = data.p2Wins;
      this.matchState = 'ended';
      if (data.winner === 0) {
        this.hudMessage = 'MATCH DRAW · FIRE TO RETRY';
      } else if (data.winner === this.playerNum) {
        this.hudMessage = 'YOU WIN · FIRE TO RETRY';
      } else {
        this.hudMessage = 'YOU LOSE · FIRE TO RETRY';
      }
      this.outcome = this.hudMessage;
      this.feedbackTimer = 2.4;
      this.room = '';
    });

    this.socket.on('rhythm_opponent_disconnected', () => {
      this.matchState = 'disconnected';
      this.hudMessage = 'OPPONENT LEFT · FIRE TO REQUEUE';
      this.outcome = 'OPPONENT LEFT';
      this.feedbackTimer = 2;
      this.room = '';
    });

    this.socket.on('disconnect', () => {
      if (this.disposed) return;
      this.matchState = 'disconnected';
      this.hudMessage = 'CONNECTION LOST · FIRE TO RETRY';
    });
  }

  private joinQueue(): void {
    if (!this.socket.connected) return;
    this.socket.emit('join_rhythm_duel');
    this.matchState = 'waiting';
    this.hudMessage = 'MATCHMAKING...';
    this.room = '';
  }

  private getMyHealth(): number {
    return this.playerNum === 1 ? this.p1Health : this.p2Health;
  }

  private getOpponentHealth(): number {
    return this.playerNum === 1 ? this.p2Health : this.p1Health;
  }

  private getMyCombo(): number {
    return this.playerNum === 1 ? this.p1Combo : this.p2Combo;
  }

  private getOpponentCombo(): number {
    return this.playerNum === 1 ? this.p2Combo : this.p1Combo;
  }

  private getActiveBeatInfo(nowMs: number): { beat: number; section: RhythmSection; sectionIndex: number; role: 'ATTACK' | 'DEFEND' } {
    const elapsed = Math.max(0, nowMs - this.startAt);
    const beat = Math.max(0, Math.floor(elapsed / this.beatMs));
    let acc = 0;
    let idx = this.sections.length > 0 ? this.sections.length - 1 : 0;
    for (let i = 0; i < this.sections.length; i++) {
      acc += this.sections[i].beats;
      if (beat < acc) {
        idx = i;
        break;
      }
    }
    const section = this.sections[idx] ?? { beats: 8, attacker: 1, label: 'P1 ATTACK' };
    const role = section.attacker === this.playerNum ? 'ATTACK' : 'DEFEND';
    return { beat, section, sectionIndex: idx, role };
  }

  private sendRhythmInput(): void {
    if (this.matchState !== 'playing' || !this.room || this.startAt <= 0) return;
    const now = Date.now();
    const beatFloat = (now - this.startAt) / this.beatMs;
    const nearest = Math.round(beatFloat);
    if (!Number.isFinite(nearest) || nearest < 0 || nearest >= this.totalBeats) return;
    if (nearest === this.lastSentBeat) return;

    const offsetMs = (beatFloat - nearest) * this.beatMs;
    this.socket.emit('rhythm_input', {
      beat: nearest,
      offsetMs,
    });
    this.lastSentBeat = nearest;
  }

  resize(w: number, h: number): void {
    resizeThree(this.kit, w, h, 24, 27);
  }

  update(dt: number, input: InputState): void {
    if (this.disposed) return;

    const bands = this.ctx.getBands();
    const now = Date.now();
    const beatInfo = this.getActiveBeatInfo(now);
    this.sectionIndex = beatInfo.sectionIndex;

    if (this.feedbackTimer > 0) this.feedbackTimer -= dt;

    if (input.firePressed) {
      if (this.matchState === 'playing') {
        this.sendRhythmInput();
      } else if (this.matchState === 'waiting' || this.matchState === 'disconnected' || this.matchState === 'ended') {
        this.joinQueue();
      }
    }

    const phaseMs = this.startAt > 0 ? (now - this.startAt) % this.beatMs : 0;
    const phase = this.beatMs > 0 ? Math.min(1, Math.max(0, phaseMs / this.beatMs)) : 0;
    const beatPulse = 1 - Math.abs(phase * 2 - 1);

    const p1Attacks = beatInfo.section.attacker === 1;
    const ring1Mat = this.roleRing1.material as THREE.MeshStandardMaterial;
    const ring2Mat = this.roleRing2.material as THREE.MeshStandardMaterial;
    ring1Mat.color.setHex(p1Attacks ? 0xff6633 : 0x2fdf84);
    ring2Mat.color.setHex(p1Attacks ? 0x2fdf84 : 0xff6633);
    ring1Mat.emissive.setHex(p1Attacks ? 0x5e1b0a : 0x1e4e2f);
    ring2Mat.emissive.setHex(p1Attacks ? 0x1e4e2f : 0x5e1b0a);

    const attackLunge = (0.26 + bands.beat * 0.2) * beatPulse;
    this.fighter1.position.x = -4.2 + (p1Attacks ? attackLunge : -attackLunge * 0.2);
    this.fighter2.position.x = 4.2 + (p1Attacks ? attackLunge * 0.2 : -attackLunge);

    const bob = 1 + bands.energy * 0.04 + beatPulse * 0.05;
    this.fighter1.scale.setScalar(bob);
    this.fighter2.scale.setScalar(bob);

    this.beatBar.scale.x = 0.38 + beatPulse * 1.25;
    this.beatBar.position.y = 6.6 + beatPulse * 0.35;

    const scheduleText = this.sections
      .map((s) => `${s.attacker === 1 ? 'P1' : 'P2'}:${s.beats}`)
      .join(' > ');

    if (this.matchState === 'playing') {
      const roundRole = beatInfo.role;
      const hpText = `HP ${this.getMyHealth()}/${this.maxHealth}-${this.getOpponentHealth()}/${this.maxHealth}`;
      const comboText = `COMBO ${this.getMyCombo()}/${this.getOpponentCombo()}`;
      const roleText = `${roundRole} · SEC ${this.sectionIndex + 1}/${Math.max(1, this.sections.length)}`;
      const outcomeText = this.feedbackTimer > 0 && this.outcome ? ` · ${this.outcome}` : '';
      this.hudMessage = `R${this.round} ${roleText} · ${hpText} · ${comboText}${outcomeText} · BO${this.roundsToWin} · SCHED ${scheduleText}`;
    } else if (this.matchState === 'waiting') {
      this.hudMessage = 'WAITING FOR OPPONENT...';
    }

    const cameraDepth = 24 - bands.energy * 0.8;
    this.kit.camera.position.z += (cameraDepth - this.kit.camera.position.z) * Math.min(1, dt * 7);

    renderThree(this.kit);

  }

  getHud() {
    const myWins = this.playerNum === 1 ? this.p1Wins : this.p2Wins;
    const oppWins = this.playerNum === 1 ? this.p2Wins : this.p1Wins;
    return {
      score: myWins,
      lives: Math.max(1, this.getMyHealth()),
      wave: oppWins,
      message: this.hudMessage,
    };
  }

  dispose(): void {
    this.disposed = true;
    if (this.socket) {
      this.socket.disconnect();
    }
    disposeObject(this.kit.root);
    this.kit.dispose();
  }
}
